import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import type { AppDocument } from '../../types/document';
import {
  DocumentsIcon,
  TaxReturnIcon,
  TaxDocumentIcon,
  HealthcareDirectiveIcon,
  HealthcarePowerOfAttorneyIcon,
  LastWillIcon,
  LivingTrustIcon,
  OtherLegalDocumentsIcon,
} from '../ui/Icons';

interface PersonalDocumentItem {
  id: string;
  category: 'documents';
  name: string;
  description: string;
  location: string;
  handlingInstructions: string;
  notes: string;
  createdAt: string;
  documents?: AppDocument[];
}

interface TaxDocument {
  id: string;
  type: 'federal' | 'state' | 'property' | 'other';
  year: string;
  description: string;
  status: 'filed' | 'pending' | 'extension';
  amount?: number;
  documents: AppDocument[];
  notes?: string;
}

interface LegalDocument {
  id: string;
  type: 'will' | 'trust' | 'healthcare_power_of_attorney' | 'healthcare_directive' | 'other';
  name: string;
  description?: string;
  uploadDate: string;
  lastModified?: string;
  notes?: string;
  documents: AppDocument[];
}

type DocumentCategoryKey = 'important_documents' | 'tax_documents' | 'legal_documents';
type DocumentFilterKey = 'all' | DocumentCategoryKey;

interface DocumentOverviewItem {
  id: string;
  categoryKey: DocumentCategoryKey;
  categoryLabel: string;
  typeLabel: string;
  title: string;
  subtitle?: string;
  details: string[];
  notes?: string;
  documentsCount: number;
  raw: PersonalDocumentItem | TaxDocument | LegalDocument;
}

const TAX_TYPES = [
  { value: 'federal', label: 'Federal Tax Returns' },
  { value: 'state', label: 'State Tax Returns' },
  { value: 'property', label: 'Property Tax Records' },
  { value: 'other', label: 'Other Tax Documents' },
];

const LEGAL_TYPES = [
  { value: 'will', label: 'Last Will and Testament' },
  { value: 'trust', label: 'Living Trust' },
  { value: 'healthcare_power_of_attorney', label: 'Healthcare Power of Attorney' },
  { value: 'healthcare_directive', label: 'Healthcare Directive' },
  { value: 'other', label: 'Other Legal Documents' },
];

const findLabel = (options: { value: string; label: string }[], value?: string) =>
  options.find((o) => o.value === value)?.label || value || '';

const CategoriesIcon = ({ className = 'w-6 h-6 text-cyan-700' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <rect x="3" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
  </svg>
);

// Executor read-only: never fall back to browser localStorage backup keys.
const loadBackupArray = <T,>(_key: string): T[] => [];

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === "object")
    .map((doc) => ({
      id: typeof doc.id === "string" ? doc.id : "",
      name: typeof doc.name === "string" ? doc.name : "Untitled document",
      mimeType: typeof doc.mimeType === "string" ? doc.mimeType : undefined,
      size: typeof doc.size === "number" ? doc.size : undefined,
      createdAt: typeof doc.createdAt === "string" ? doc.createdAt : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const normalizeLegacyLegalDocuments = (docs: unknown[] = []): LegalDocument[] =>
  docs.map((doc) => {
    const normalizedDocuments =
      Array.isArray(doc.documents) && doc.documents.length > 0
        ? normalizeDocuments(doc.documents)
        : [];

    return {
      id: doc.id,
      type: doc.type,
      name: doc.name,
      description: doc.description,
      uploadDate: doc.uploadDate,
      lastModified: doc.lastModified,
      notes: doc.notes,
      documents: normalizedDocuments,
    } as LegalDocument;
  });

const getDocumentIcon = (
  categoryKey: DocumentCategoryKey,
  type?: string,
  className = 'w-6 h-6 text-cyan-700'
) => {
  if (categoryKey === 'important_documents') {
    return <DocumentsIcon className={className} />;
  }

  if (categoryKey === 'tax_documents') {
    if (type === 'federal') return <TaxReturnIcon className={className} />;
    return <TaxDocumentIcon className={className} />;
  }

  if (categoryKey === 'legal_documents') {
    switch (type) {
      case 'will':
        return <LastWillIcon className={className} />;
      case 'trust':
        return <LivingTrustIcon className={className} />;
      case 'healthcare_power_of_attorney':
        return <HealthcarePowerOfAttorneyIcon className={className} />;
      case 'healthcare_directive':
        return <HealthcareDirectiveIcon className={className} />;
      default:
        return <OtherLegalDocumentsIcon className={className} />;
    }
  }

  return <DocumentsIcon className={className} />;
};

const getAttachedDocuments = (item: DocumentOverviewItem): AppDocument[] => {
  switch (item.categoryKey) {
    case 'important_documents':
      return normalizeDocuments((item.raw as PersonalDocumentItem).documents || []);
    case 'tax_documents':
      return normalizeDocuments((item.raw as TaxDocument).documents || []);
    case 'legal_documents':
      return normalizeDocuments((item.raw as LegalDocument).documents || []);
    default:
      return [];
  }
};

const EFrame144: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const estateOwnerName = getPlanOwnerName(session.name);

  const [loading, setLoading] = useState(true);
  const [importantDocuments, setImportantDocuments] = useState<PersonalDocumentItem[]>([]);
  const [taxDocuments, setTaxDocuments] = useState<TaxDocument[]>([]);
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<DocumentFilterKey>('all');

  useEffect(() => {
    const personalAnswers = session?.taskProgress?.personal?.answers || {};
    const taxAnswers = session?.taskProgress?.tax_documents?.answers || {};
    const legalAnswers = session?.taskProgress?.legal_documents?.answers || {};

    const sessionPersonalItems = (personalAnswers.personalItems as PersonalDocumentItem[]) || [];

    const filteredImportantDocs = sessionPersonalItems
      .filter((item) => item.category === 'documents')
      .map((item) => ({
        ...item,
        documents: normalizeDocuments(item.documents || []),
      }));

    setImportantDocuments(
      filteredImportantDocs.length
        ? filteredImportantDocs
        : loadBackupArray<PersonalDocumentItem>('personal-items-backup')
            .filter((item) => item.category === 'documents')
            .map((item) => ({
              ...item,
              documents: normalizeDocuments(item.documents || []),
            }))
    );

    setTaxDocuments(
      ((taxAnswers.documents as TaxDocument[]) || []).length
        ? ((taxAnswers.documents as TaxDocument[]) || []).map((doc) => ({
            ...doc,
            documents: normalizeDocuments(doc.documents || []),
          }))
        : loadBackupArray<TaxDocument>('tax-documents-backup').map((doc) => ({
            ...doc,
            documents: normalizeDocuments(doc.documents || []),
          }))
    );

    setLegalDocuments(
      ((legalAnswers.documents as unknown[]) || []).length
        ? normalizeLegacyLegalDocuments((legalAnswers.documents as unknown[]) || [])
        : normalizeLegacyLegalDocuments(loadBackupArray<Record<string, unknown>>('legal-documents-backup'))
    );

    setLoading(false);
  }, [
    session?.taskProgress?.personal,
    session?.taskProgress?.tax_documents,
    session?.taskProgress?.legal_documents,
  ]);

  const overviewItems: DocumentOverviewItem[] = useMemo(() => {
    const important: DocumentOverviewItem[] = importantDocuments.map((doc) => ({
      id: doc.id,
      categoryKey: 'important_documents',
      categoryLabel: 'Important Documents',
      typeLabel: 'Important Document',
      title: doc.name,
      subtitle: doc.location || 'No location listed',
      details: [
        doc.description ? `Description: ${doc.description}` : '',
        doc.handlingInstructions ? `Handling: ${doc.handlingInstructions}` : '',
      ].filter(Boolean),
      notes: doc.notes,
      documentsCount: doc.documents?.length || 0,
      raw: doc,
    }));

    const tax: DocumentOverviewItem[] = taxDocuments.map((doc) => ({
      id: doc.id,
      categoryKey: 'tax_documents',
      categoryLabel: 'Tax Documents',
      typeLabel: findLabel(TAX_TYPES, doc.type),
      title: `${doc.description} (${doc.year})`,
      subtitle: `Status: ${doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}`,
      details: [
        doc.amount !== undefined ? `Amount: $${doc.amount.toLocaleString()}` : '',
      ].filter(Boolean),
      notes: doc.notes,
      documentsCount: doc.documents?.length || 0,
      raw: doc,
    }));

    const legal: DocumentOverviewItem[] = legalDocuments.map((doc) => ({
      id: doc.id,
      categoryKey: 'legal_documents',
      categoryLabel: 'Legal Documents',
      typeLabel: findLabel(LEGAL_TYPES, doc.type),
      title: doc.name,
      subtitle: doc.uploadDate ? `Uploaded ${new Date(doc.uploadDate).toLocaleDateString()}` : '',
      details: [],
      notes: doc.notes || doc.description,
      documentsCount: doc.documents?.length || 0,
      raw: doc,
    }));

    return [...important, ...tax, ...legal];
  }, [importantDocuments, taxDocuments, legalDocuments]);

  const filteredItems = useMemo(() => {
    if (selectedFilter === 'all') return overviewItems;
    return overviewItems.filter((item) => item.categoryKey === selectedFilter);
  }, [overviewItems, selectedFilter]);

  const totalDocuments = overviewItems.length;
  const totalCategories = [
    importantDocuments.length > 0,
    taxDocuments.length > 0,
    legalDocuments.length > 0,
  ].filter(Boolean).length;

  const statCards = [
    {
      label: 'Total Documents',
      value: totalDocuments,
      icon: <DocumentsIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Categories',
      value: totalCategories,
      icon: <CategoriesIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px] min-w-0 overflow-x-hidden">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <ResponsiveHeader title={`${estateOwnerName}’s Documents & Records`} />
            </div>
          </div>

          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed break-words">
            Access important documents tied to {estateOwnerName}’s estate, including wills, tax records, and essential files.
          </p>

          <p className="mt-3 text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed break-words">
            Having these organized ensures you can act quickly and meet legal and financial requirements.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col">
                    <div className="font-sans text-base text-stone-600">{card.label}</div>
                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4">
                      {loading ? '—' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                <h2 className="font-sans text-xl font-medium text-stone-800">
                  All Documents
                </h2>
              </div>

              <div className="shrink-0">
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value as DocumentFilterKey)}
                  className="min-w-[220px] px-3 py-2 border border-stone-300 rounded-md bg-white text-sm text-stone-800"
                >
                  <option value="all">All ({overviewItems.length})</option>
                  <option value="important_documents">
                    Important Documents ({importantDocuments.length})
                  </option>
                  <option value="tax_documents">
                    Tax Documents ({taxDocuments.length})
                  </option>
                  <option value="legal_documents">
                    Legal Documents ({legalDocuments.length})
                  </option>
                </select>
              </div>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading documents...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No documents found for this filter yet.
                  </p>
                </div>
              ) : (
                filteredItems.map((item) => {
                  const attachedDocuments = getAttachedDocuments(item);
                  const itemType =
                    item.categoryKey === 'tax_documents' || item.categoryKey === 'legal_documents'
                      ? (item.raw as TaxDocument | LegalDocument).type
                      : undefined;

                  return (
                    <div
                      key={`${item.categoryKey}-${item.id}`}
                      className="flex flex-col sm:flex-row sm:items-start gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 overflow-hidden max-w-full"
                    >
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0">
                        <div className="shrink-0 mt-0.5">
                          {getDocumentIcon(item.categoryKey, itemType, 'w-6 h-6 text-cyan-700')}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-sans text-lg font-medium text-stone-800 break-words min-w-0">
                              {item.title}
                            </div>
                            <span className="inline-flex items-center rounded-full bg-stone-200 text-stone-800 px-2.5 py-1 text-xs font-medium">
                              {item.categoryLabel}
                            </span>
                          </div>

                          <div className="font-sans text-base text-stone-600 mt-1">
                            {item.typeLabel}
                          </div>

                          {item.subtitle && (
                            <div className="font-sans text-base text-stone-600 mt-1">
                              {item.subtitle}
                            </div>
                          )}

                          {item.details.length > 0 && (
                            <div className="flex flex-wrap gap-x-5 gap-y-1 font-sans text-sm sm:text-base text-stone-600 mt-1 break-words">
                              {item.details.map((detail, index) => (
                                <span key={index}>{detail}</span>
                              ))}
                            </div>
                          )}

                          {item.notes && (
                            <div className="font-sans text-sm text-stone-500 mt-2">
                              {item.notes}
                            </div>
                          )}

                          {attachedDocuments.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <div className="font-sans text-sm text-cyan-700">
                                {attachedDocuments.length} document(s) attached
                              </div>

                              <div className="flex flex-col gap-2">
                                {attachedDocuments.map((doc, index) => (
                                  <div
                                    key={`${item.id}-file-${doc.id}-${index}`}
                                    className="flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50 px-3 py-2"
                                  >
                                    {getDocumentIcon(
                                      item.categoryKey,
                                      itemType,
                                      "w-4 h-4 text-cyan-700 shrink-0 mt-0.5"
                                    )}

                                    <span
                                      className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
                                      title={doc.name}
                                    >
                                      {doc.name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame144;